import { useState, useEffect } from 'react'
import { useTranslation } from '@/hooks/useTypedTranslation'
import { useMultilingualCategories } from '@/hooks/useMultilingualCategories'
import { 
  useCreateMultilingualProject, 
  useUpdateMultilingualProject,
  MultilingualProjectItem 
} from '@/hooks/useMultilingualProjects'
import { supabase } from '@/integrations/supabase/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Loader2, X, Globe, Save, Languages, Upload, ImageIcon, Trash2, Plus } from 'lucide-react'
import { toast } from 'sonner'

// Image uploader component for cover image
function ImageUploader({
  value,
  onChange,
  label
}: {
  value: string
  onChange: (url: string) => void
  label: string
}) {
  const [uploading, setUploading] = useState(false)
  const [dragActive, setDragActive] = useState(false)
  
  const handleUpload = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file')
      return
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB')
      return
    }
    
    setUploading(true)
    try {
      const fileExt = file.name.split('.').pop()
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`
      const filePath = `covers/${fileName}`
      
      const { error: uploadError } = await supabase.storage
        .from('project-images')
        .upload(filePath, file)
      
      if (uploadError) throw uploadError
      
      const { data: { publicUrl } } = supabase.storage
        .from('project-images')
        .getPublicUrl(filePath)
      
      onChange(publicUrl)
      toast.success('Image uploaded successfully')
    } catch (error: any) {
      console.error('Upload failed:', error)
      toast.error(error.message || 'Failed to upload image')
    } finally {
      setUploading(false)
    }
  }
  
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) handleUpload(file)
  }
  
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setDragActive(false)
    const file = e.dataTransfer.files?.[0]
    if (file) handleUpload(file)
  }
  
  const handleRemove = () => {
    onChange('/placeholder.svg')
  }
  
  const hasImage = value && value !== '/placeholder.svg'
  
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      
      {hasImage ? (
        <div className="relative group">
          <img 
            src={value} 
            alt="Cover" 
            className="w-full h-32 object-cover rounded-lg border"
          />
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-2">
            <label className="cursor-pointer">
              <input 
                type="file" 
                accept="image/*" 
                className="hidden" 
                onChange={handleFileChange}
                disabled={uploading}
              />
              <Button variant="secondary" size="sm" asChild>
                <span>
                  <Upload className="h-4 w-4 mr-1" />
                  Replace
                </span>
              </Button>
            </label>
            <Button variant="destructive" size="sm" onClick={handleRemove}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      ) : (
        <label
          className={`
            flex flex-col items-center justify-center w-full h-32
            border-2 border-dashed rounded-lg cursor-pointer
            transition-colors
            ${dragActive 
              ? 'border-primary bg-primary/5' 
              : 'border-muted-foreground/25 hover:border-primary/50 hover:bg-muted/50'
            }
            ${uploading ? 'pointer-events-none opacity-50' : ''}
          `}
          onDragOver={(e) => { e.preventDefault(); setDragActive(true) }}
          onDragLeave={() => setDragActive(false)}
          onDrop={handleDrop}
        >
          <input 
            type="file" 
            accept="image/*" 
            className="hidden" 
            onChange={handleFileChange}
            disabled={uploading}
          />
          {uploading ? (
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          ) : (
            <>
              <ImageIcon className="h-6 w-6 text-muted-foreground mb-1" />
              <p className="text-xs text-muted-foreground">Drop or click to upload</p>
            </>
          )}
        </label>
      )}
    </div>
  )
}

// Gallery uploader component for multiple images
function GalleryUploader({
  value,
  onChange,
  label
}: {
  value: string[]
  onChange: (urls: string[]) => void
  label: string
}) {
  const [uploading, setUploading] = useState(false)
  
  const handleUpload = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file')
      return
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB')
      return
    }
    
    setUploading(true)
    try {
      const fileExt = file.name.split('.').pop()
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`
      const filePath = `gallery/${fileName}`
      
      const { error: uploadError } = await supabase.storage
        .from('project-images')
        .upload(filePath, file)
      
      if (uploadError) throw uploadError
      
      const { data: { publicUrl } } = supabase.storage
        .from('project-images')
        .getPublicUrl(filePath)
      
      onChange([...value, publicUrl])
      toast.success('Image added to gallery')
    } catch (error: any) {
      console.error('Upload failed:', error)
      toast.error(error.message || 'Failed to upload image')
    } finally {
      setUploading(false)
    }
  }
  
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) handleUpload(file)
  }
  
  const handleRemove = (index: number) => {
    const newGallery = value.filter((_, i) => i !== index)
    onChange(newGallery)
  }
  
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      
      <div className="grid grid-cols-4 gap-2">
        {value.map((url, index) => (
          <div key={index} className="relative group aspect-square">
            <img 
              src={url} 
              alt={`Gallery ${index + 1}`} 
              className="w-full h-full object-cover rounded-lg border"
            />
            <button
              onClick={() => handleRemove(index)}
              className="absolute top-1 right-1 p-1 bg-destructive text-destructive-foreground rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        ))}
        
        <label
          className={`
            flex flex-col items-center justify-center aspect-square
            border-2 border-dashed rounded-lg cursor-pointer
            transition-colors
            border-muted-foreground/25 hover:border-primary/50 hover:bg-muted/50
            ${uploading ? 'pointer-events-none opacity-50' : ''}
          `}
        >
          <input 
            type="file" 
            accept="image/*" 
            className="hidden" 
            onChange={handleFileChange}
            disabled={uploading}
          />
          {uploading ? (
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          ) : (
            <Plus className="h-5 w-5 text-muted-foreground" />
          )}
        </label>
      </div>
    </div>
  )
}

interface ProjectTranslations {
  zh: {
    title: string
    subtitle: string
    description: string
    content: string
  }
  en: {
    title: string
    subtitle: string
    description: string
    content: string
  }
}

interface ProjectData {
  id: string
  category_id: string
  status: string
  image: string
  gallery: string[]
  link: string
  tags: string[]
  features: string[]
  tech_stack: string[]
  sort_order: number
  is_featured: boolean
}

interface MultilingualProjectEditorProps {
  project?: MultilingualProjectItem | null
  onClose: () => void
  onSuccess?: () => void
}

export function MultilingualProjectEditor({ project, onClose, onSuccess }: MultilingualProjectEditorProps) {
  const { t, i18n } = useTranslation()
  const currentLanguage = i18n.language as 'zh' | 'en'
  const { data: categories } = useMultilingualCategories(currentLanguage)
  
  const createProject = useCreateMultilingualProject()
  const updateProject = useUpdateMultilingualProject()
  
  const isEditing = !!project
  
  // Project base data
  const [projectData, setProjectData] = useState<ProjectData>({
    id: '',
    category_id: '',
    status: 'planned',
    image: '/placeholder.svg',
    gallery: [],
    link: '',
    tags: [],
    features: [],
    tech_stack: [],
    sort_order: 0,
    is_featured: false
  })
  
  // Translations
  const [translations, setTranslations] = useState<ProjectTranslations>({
    zh: { title: '', subtitle: '', description: '', content: '' },
    en: { title: '', subtitle: '', description: '', content: '' }
  })
  
  const [loading, setLoading] = useState(false)
  const [loadingTranslations, setLoadingTranslations] = useState(false)
  
  // Load existing project data
  useEffect(() => {
    if (project) {
      setProjectData({
        id: project.id,
        category_id: project.category_id,
        status: project.status,
        image: project.image,
        gallery: project.gallery || [],
        link: project.link || '',
        tags: project.tags || [],
        features: project.features || [],
        tech_stack: project.tech_stack || [],
        sort_order: project.sort_order,
        is_featured: project.is_featured
      })
      
      // Load both translations
      loadTranslations(project.id)
    }
  }, [project])
  
  // Set default category when categories load
  useEffect(() => {
    if (categories && categories.length > 0 && !projectData.category_id) {
      setProjectData(prev => ({ ...prev, category_id: categories[0].id }))
    }
  }, [categories])
  
  const loadTranslations = async (projectId: string) => {
    setLoadingTranslations(true)
    try {
      const { data, error } = await supabase
        .from('project_translations')
        .select('*')
        .eq('project_id', projectId)
      
      if (error) throw error
      
      const zhTrans = data?.find(t => t.language === 'zh')
      const enTrans = data?.find(t => t.language === 'en')
      
      setTranslations({
        zh: {
          title: zhTrans?.title || '',
          subtitle: zhTrans?.subtitle || '',
          description: zhTrans?.description || '',
          content: zhTrans?.content || ''
        },
        en: {
          title: enTrans?.title || '',
          subtitle: enTrans?.subtitle || '',
          description: enTrans?.description || '',
          content: enTrans?.content || ''
        }
      })
    } catch (error) {
      console.error('Failed to load translations:', error)
      toast.error('Failed to load translations')
    } finally {
      setLoadingTranslations(false)
    }
  }
  
  const handleSave = async () => {
    // Validation
    if (!projectData.id.trim()) {
      toast.error('Project ID is required')
      return
    }
    if (!translations.zh.title.trim() || !translations.en.title.trim()) {
      toast.error('Title is required in both languages')
      return
    }
    if (!translations.zh.description.trim() || !translations.en.description.trim()) {
      toast.error('Description is required in both languages')
      return
    }
    
    setLoading(true)
    try {
      if (isEditing) {
        await updateProject.mutateAsync({
          id: projectData.id,
          project: {
            category_id: projectData.category_id,
            status: projectData.status,
            image: projectData.image,
            gallery: projectData.gallery,
            link: projectData.link || null,
            tags: projectData.tags,
            features: projectData.features,
            tech_stack: projectData.tech_stack,
            sort_order: projectData.sort_order,
            is_featured: projectData.is_featured
          },
          translations: {
            zh: translations.zh,
            en: translations.en
          }
        })
        toast.success('Project updated successfully')
      } else {
        await createProject.mutateAsync({
          project: {
            id: projectData.id,
            category_id: projectData.category_id,
            status: projectData.status,
            image: projectData.image,
            gallery: projectData.gallery,
            link: projectData.link || undefined,
            tags: projectData.tags,
            features: projectData.features,
            tech_stack: projectData.tech_stack,
            sort_order: projectData.sort_order,
            is_featured: projectData.is_featured
          },
          translations: {
            zh: translations.zh,
            en: translations.en
          }
        })
        toast.success('Project created successfully')
      }
      onSuccess?.()
      onClose()
    } catch (error: any) {
      console.error('Failed to save project:', error)
      toast.error(error.message || 'Failed to save project')
    } finally {
      setLoading(false)
    }
  }
  
  const updateTranslation = (lang: 'zh' | 'en', field: keyof ProjectTranslations['zh'], value: string) => {
    setTranslations(prev => ({
      ...prev,
      [lang]: { ...prev[lang], [field]: value }
    }))
  }
  
  const parseArrayInput = (value: string): string[] => {
    try {
      const parsed = JSON.parse(value)
      return Array.isArray(parsed) ? parsed : []
    } catch {
      return value.split(',').map(s => s.trim()).filter(Boolean)
    }
  }

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Languages className="h-6 w-6 text-primary" />
          <h2 className="text-xl font-bold">
            {isEditing ? 'Edit Multilingual Project' : 'Create Multilingual Project'}
          </h2>
        </div>
        <Button variant="ghost" size="sm" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>
      
      {loadingTranslations ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="space-y-6">
          {/* Base Project Info */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Globe className="h-4 w-4" />
                Basic Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="id">Project ID</Label>
                  <Input
                    id="id"
                    value={projectData.id}
                    onChange={e => setProjectData(prev => ({ ...prev, id: e.target.value }))}
                    disabled={isEditing}
                    placeholder="unique-project-id"
                  />
                </div>
                <div>
                  <Label htmlFor="category">Category</Label>
                  <Select 
                    value={projectData.category_id} 
                    onValueChange={value => setProjectData(prev => ({ ...prev, category_id: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories?.map(cat => (
                        <SelectItem key={cat.id} value={cat.id}>{cat.title}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="status">Status</Label>
                  <Select 
                    value={projectData.status} 
                    onValueChange={value => setProjectData(prev => ({ ...prev, status: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="in-progress">In Progress</SelectItem>
                      <SelectItem value="planned">Planned</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <ImageUploader
                  value={projectData.image}
                  onChange={(url) => setProjectData(prev => ({ ...prev, image: url }))}
                  label="Cover Image"
                />
                <div>
                  <Label htmlFor="link">Project Link</Label>
                  <Input
                    id="link"
                    value={projectData.link}
                    onChange={e => setProjectData(prev => ({ ...prev, link: e.target.value }))}
                    placeholder="https://..."
                  />
                </div>
              </div>
              
              <GalleryUploader
                value={projectData.gallery}
                onChange={(urls) => setProjectData(prev => ({ ...prev, gallery: urls }))}
                label="Gallery Images"
              />
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="tags">Tags (comma-separated or JSON)</Label>
                  <Input
                    id="tags"
                    defaultValue={JSON.stringify(projectData.tags)}
                    onChange={e => setProjectData(prev => ({ ...prev, tags: parseArrayInput(e.target.value) }))}
                    placeholder='["tag1", "tag2"] or tag1, tag2'
                  />
                </div>
                <div>
                  <Label htmlFor="tech_stack">Tech Stack</Label>
                  <Input
                    id="tech_stack"
                    defaultValue={JSON.stringify(projectData.tech_stack)}
                    onChange={e => setProjectData(prev => ({ ...prev, tech_stack: parseArrayInput(e.target.value) }))}
                    placeholder='["React", "TypeScript"]'
                  />
                </div>
                <div>
                  <Label htmlFor="sort_order">Sort Order</Label>
                  <Input
                    id="sort_order"
                    type="number"
                    value={projectData.sort_order}
                    onChange={e => setProjectData(prev => ({ ...prev, sort_order: parseInt(e.target.value) || 0 }))}
                  />
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <Switch
                  id="is_featured"
                  checked={projectData.is_featured}
                  onCheckedChange={checked => setProjectData(prev => ({ ...prev, is_featured: checked }))}
                />
                <Label htmlFor="is_featured">Featured Project</Label>
              </div>
            </CardContent>
          </Card>
          
          {/* Multilingual Content - Side by Side */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Languages className="h-4 w-4" />
                Multilingual Content
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="side-by-side" className="w-full">
                <TabsList className="mb-4">
                  <TabsTrigger value="side-by-side">Side by Side</TabsTrigger>
                  <TabsTrigger value="chinese">Chinese Only</TabsTrigger>
                  <TabsTrigger value="english">English Only</TabsTrigger>
                </TabsList>
                
                <TabsContent value="side-by-side" className="mt-0">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Chinese Column */}
                    <div className="space-y-4 p-4 border rounded-lg bg-red-50/30 dark:bg-red-950/10">
                      <div className="flex items-center gap-2 text-red-600 dark:text-red-400 font-medium mb-4">
                        <span className="text-lg">🇨🇳</span>
                        <span>中文 (Chinese)</span>
                      </div>
                      
                      <div>
                        <Label>标题 (Title) *</Label>
                        <Input
                          value={translations.zh.title}
                          onChange={e => updateTranslation('zh', 'title', e.target.value)}
                          placeholder="项目标题"
                        />
                      </div>
                      
                      <div>
                        <Label>副标题 (Subtitle)</Label>
                        <Input
                          value={translations.zh.subtitle}
                          onChange={e => updateTranslation('zh', 'subtitle', e.target.value)}
                          placeholder="项目副标题"
                        />
                      </div>
                      
                      <div>
                        <Label>描述 (Description) *</Label>
                        <Textarea
                          value={translations.zh.description}
                          onChange={e => updateTranslation('zh', 'description', e.target.value)}
                          placeholder="项目描述..."
                          rows={3}
                        />
                      </div>
                      
                      <div>
                        <Label>内容 (Content)</Label>
                        <Textarea
                          value={translations.zh.content}
                          onChange={e => updateTranslation('zh', 'content', e.target.value)}
                          placeholder="详细内容..."
                          rows={6}
                        />
                      </div>
                    </div>
                    
                    {/* English Column */}
                    <div className="space-y-4 p-4 border rounded-lg bg-blue-50/30 dark:bg-blue-950/10">
                      <div className="flex items-center gap-2 text-blue-600 dark:text-blue-400 font-medium mb-4">
                        <span className="text-lg">🇺🇸</span>
                        <span>English</span>
                      </div>
                      
                      <div>
                        <Label>Title *</Label>
                        <Input
                          value={translations.en.title}
                          onChange={e => updateTranslation('en', 'title', e.target.value)}
                          placeholder="Project title"
                        />
                      </div>
                      
                      <div>
                        <Label>Subtitle</Label>
                        <Input
                          value={translations.en.subtitle}
                          onChange={e => updateTranslation('en', 'subtitle', e.target.value)}
                          placeholder="Project subtitle"
                        />
                      </div>
                      
                      <div>
                        <Label>Description *</Label>
                        <Textarea
                          value={translations.en.description}
                          onChange={e => updateTranslation('en', 'description', e.target.value)}
                          placeholder="Project description..."
                          rows={3}
                        />
                      </div>
                      
                      <div>
                        <Label>Content</Label>
                        <Textarea
                          value={translations.en.content}
                          onChange={e => updateTranslation('en', 'content', e.target.value)}
                          placeholder="Detailed content..."
                          rows={6}
                        />
                      </div>
                    </div>
                  </div>
                </TabsContent>
                
                <TabsContent value="chinese" className="mt-0">
                  <div className="space-y-4 p-4 border rounded-lg bg-red-50/30 dark:bg-red-950/10 max-w-2xl">
                    <div className="flex items-center gap-2 text-red-600 dark:text-red-400 font-medium mb-4">
                      <span className="text-lg">🇨🇳</span>
                      <span>中文 (Chinese)</span>
                    </div>
                    
                    <div>
                      <Label>标题 (Title) *</Label>
                      <Input
                        value={translations.zh.title}
                        onChange={e => updateTranslation('zh', 'title', e.target.value)}
                        placeholder="项目标题"
                      />
                    </div>
                    
                    <div>
                      <Label>副标题 (Subtitle)</Label>
                      <Input
                        value={translations.zh.subtitle}
                        onChange={e => updateTranslation('zh', 'subtitle', e.target.value)}
                        placeholder="项目副标题"
                      />
                    </div>
                    
                    <div>
                      <Label>描述 (Description) *</Label>
                      <Textarea
                        value={translations.zh.description}
                        onChange={e => updateTranslation('zh', 'description', e.target.value)}
                        placeholder="项目描述..."
                        rows={3}
                      />
                    </div>
                    
                    <div>
                      <Label>内容 (Content)</Label>
                      <Textarea
                        value={translations.zh.content}
                        onChange={e => updateTranslation('zh', 'content', e.target.value)}
                        placeholder="详细内容..."
                        rows={8}
                      />
                    </div>
                  </div>
                </TabsContent>
                
                <TabsContent value="english" className="mt-0">
                  <div className="space-y-4 p-4 border rounded-lg bg-blue-50/30 dark:bg-blue-950/10 max-w-2xl">
                    <div className="flex items-center gap-2 text-blue-600 dark:text-blue-400 font-medium mb-4">
                      <span className="text-lg">🇺🇸</span>
                      <span>English</span>
                    </div>
                    
                    <div>
                      <Label>Title *</Label>
                      <Input
                        value={translations.en.title}
                        onChange={e => updateTranslation('en', 'title', e.target.value)}
                        placeholder="Project title"
                      />
                    </div>
                    
                    <div>
                      <Label>Subtitle</Label>
                      <Input
                        value={translations.en.subtitle}
                        onChange={e => updateTranslation('en', 'subtitle', e.target.value)}
                        placeholder="Project subtitle"
                      />
                    </div>
                    
                    <div>
                      <Label>Description *</Label>
                      <Textarea
                        value={translations.en.description}
                        onChange={e => updateTranslation('en', 'description', e.target.value)}
                        placeholder="Project description..."
                        rows={3}
                      />
                    </div>
                    
                    <div>
                      <Label>Content</Label>
                      <Textarea
                        value={translations.en.content}
                        onChange={e => updateTranslation('en', 'content', e.target.value)}
                        placeholder="Detailed content..."
                        rows={8}
                      />
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
          
          {/* Actions */}
          <div className="flex justify-end gap-4">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={loading}>
              {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              <Save className="h-4 w-4 mr-2" />
              {isEditing ? 'Save Changes' : 'Create Project'}
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
